import { FC, useMemo, useState } from 'react';
import { SelectDropdown } from '@shared/ui/select-dropdawn';
import { useToast } from '@shared/hooks/useToast';
import { useConfirm } from '@shared/hooks/useConfirm';
import { useFetchDeleteTicket } from '@features/api/use-delete-tickets';
import { EventSettings } from '@features/api/use-get-settings';
import { isDatePassed, isDatePassedDateOnly } from '@shared/libs/date-validation';

export interface ParticipantsActionsDropdownProps {
  checkedUsers: { [ticketId: number]: number };
  setCheckedUsers: (
    value: { [ticketId: number]: number } | ((prev: { [ticketId: number]: number }) => { [ticketId: number]: number })
  ) => void;
  sendTickets: (users: number[], tickets?: number[]) => void;
  sendInvitations?: (users: number[], tickets?: number[]) => void;
  onMoveToSegment?: (selectedTickets: number[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onTicketDelete?: (ticketId: number) => void;
  eventDateEnd?: string;
  eventSettings?: EventSettings | null;
  refetchCategories?: () => void;
}

export const ParticipantsActionsDropdown: FC<ParticipantsActionsDropdownProps> = ({
  checkedUsers,
  setCheckedUsers,
  sendTickets,
  sendInvitations,
  onMoveToSegment,
  onLoadingChange,
  onTicketDelete,
  eventDateEnd,
  eventSettings,
  refetchCategories,
}) => {
  const { showToast } = useToast();
  const { openConfirmDialog } = useConfirm();
  const { deleteTickets } = useFetchDeleteTicket();
  const selectedUsersCount = useMemo(() => Object.values(checkedUsers).filter((item) => item).length, [checkedUsers]);
  const isNoUsersSelected = !selectedUsersCount;

  const isEventEnded = useMemo(() => isDatePassed(eventDateEnd), [eventDateEnd]);

  const isInvitationDatePassed = useMemo(
    () => isDatePassedDateOnly(eventSettings?.invitationExpiresAt),
    [eventSettings?.invitationExpiresAt]
  );

  const isTicketSendDatePassed = useMemo(
    () => isDatePassedDateOnly(eventSettings?.sendTicketAfterAcceptAt),
    [eventSettings?.sendTicketAfterAcceptAt]
  );

  const isInvitationResendBlockedBySentTickets = isInvitationDatePassed && isTicketSendDatePassed;
  const isSendTicketsDisabled = isNoUsersSelected || isEventEnded;
  const isSendInvitationsDisabled = isNoUsersSelected || isEventEnded || isInvitationResendBlockedBySentTickets;

  const ticketsTooltip = useMemo(() => {
    if (!isEventEnded) return undefined;
    return 'Мероприятие завершилось. Отправка приглашений и\n билетов невозможна';
  }, [isEventEnded]);

  const invitationsTooltip = useMemo(() => {
    if (!isEventEnded && !isInvitationResendBlockedBySentTickets) return undefined;
    if (isEventEnded) {
      return 'Мероприятие завершилось. Отправка приглашений и\n билетов невозможна';
    }
    return 'Билеты отправлены участникам. Выслать приглашение \n невозможно';
  }, [isEventEnded, isInvitationResendBlockedBySentTickets]);

  const options = useMemo(
    () => [
      {
        label: 'Отправить билеты выбранным',
        value: 'sendTickets',
        disabled: isSendTicketsDisabled,
        tooltip: ticketsTooltip,
      },
      {
        label: 'Отправить приглашения выбранным',
        value: 'sendInvitations',
        disabled: isSendInvitationsDisabled,
        tooltip: invitationsTooltip,
      },
      { label: 'Удалить выбранных', value: 'deleteSelected', disabled: isNoUsersSelected },
      { label: 'Переместить в сегмент', value: 'updateTickets', disabled: isNoUsersSelected },
    ],
    [isSendTicketsDisabled, isSendInvitationsDisabled, isNoUsersSelected, ticketsTooltip, invitationsTooltip]
  );
  const [selected, setSelected] = useState('type1');

  const sendTicketHandler = () => {
    const users = Object.values(checkedUsers);
    const tickets = Object.keys(checkedUsers).map(Number);
    sendTickets(users, tickets);
  };

  const sendInvitationHandler = () => {
    if (!sendInvitations) return;
    const users = Object.values(checkedUsers);
    const tickets = Object.keys(checkedUsers).map(Number);
    sendInvitations(users, tickets);
  };

  const handleDeleteSelectedParticipants = async () => {
    const allCheckedTicketIds = Object.keys(checkedUsers).map(Number);
    const allCheckedUsers = Object.values(checkedUsers);

    if (allCheckedUsers.length === 0) {
      showToast('Нет выбранных участников для удаления', 'success');
      return;
    }

    const message = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>Вы собираетесь удалить {allCheckedUsers.length} участников?</div>
        <div>Данное действие нельзя отменить</div>
      </div>
    );

    const res = await openConfirmDialog(message);
    if (!res) return;

    try {
      onLoadingChange?.(true);
      const response = await deleteTickets({ ticketIds: allCheckedTicketIds });
      if (!response.data?.deleteTickets.success) {
        showToast('Ошибка при удалении участников', 'error');
        onLoadingChange?.(false);
        return;
      }
      allCheckedTicketIds.forEach((id) => onTicketDelete?.(id));
      setCheckedUsers({});
      refetchCategories?.();
      onLoadingChange?.(false);
    } catch {
      showToast('Произошла ошибка при удалении участников', 'error');
      onLoadingChange?.(false);
    }
  };

  const handleDropdownAction = (val: string) => {
    setSelected(val);
    switch (val) {
      case 'sendTickets':
        sendTicketHandler();
        break;
      case 'sendInvitations':
        sendInvitationHandler();
        break;
      case 'deleteSelected':
        void handleDeleteSelectedParticipants();
        break;
      case 'updateTickets': {
        const selectedTickets = Object.keys(checkedUsers).map(Number);
        onMoveToSegment?.(selectedTickets);
        break;
      }
      default:
        break;
    }
  };

  return (
    <SelectDropdown
      options={options}
      value={selected}
      onChange={handleDropdownAction}
      selectedCount={selectedUsersCount}
      settings={{
        height: '40px',
        padding: '8px 55px',
        fontSize: '16px',
      }}
    />
  );
};
